"use client";
import { fontWeights } from "@/utils/fonts";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import Link from "next/link";
import AllIconsComponent from "../custom-icons/AllIconsComponent";

// Resolves image URL from either a plain string or a { url, key } object
const resolveImageUrl = (img: any): string => {
    if (!img) return '';
    if (typeof img === 'string') return img;
    return img?.url || '';
};

export default function SmartSolution({
    pillText,
    title,
    description,
    stats,
    heroBg,
    workImage,
    certificates,
    hireBtnText,
    bookDemoText,
    bookDemoLink
}: {
    pillText?: string;
    title?: string;
    description?: string;
    stats?: any[];
    heroBg?: string;
    workImage?: string;
    certificates?: string[];
    hireBtnText?: string;
    bookDemoText?: string;
    bookDemoLink?: string;
}) {
    const finalStats = stats || [];
    const finalCertificates = certificates || [];

    return (
        <section
            className="
                relative
                w-full
                overflow-hidden
                pb-0
                flex
                flex-col
                sm:min-h-[700px]
                md:min-h-[759px]
                lg:min-h-[800px]
            "
        >
            {/* Background Image */}
            <div className="absolute inset-0 z-0" >
                {heroBg && (
                    <Image
                        src={resolveImageUrl(heroBg)}
                        alt="Hero Background"
                        fill
                        priority
                        className="object-cover"
                    />
                )}
            </div>
            <div className="relative flex-grow max-w-[1905px] mx-auto w-full flex flex-col">
                <div
                    className="
                            px-4
                            sm:px-[30px]
                            md:px-[0px]
                            relative
                            z-10
                            flex-grow
                            flex flex-col
                        ">
                    <div
                        className="text-center flex flex-col items-center flex-grow
                             pt-6
                             sm:pt-8
                             md:pt-[40px]
                             lg:pt-[50px]
                             xl:pt-[60px]
                        ">
                        {/* Top Pill Badge */}
                        <div
                            className="max-w-full border-[1.5px] border-white text-center inline-flex items-center justify-center gap-2 sm:gap-3 rounded-full bg-[#E8F0FE]
                                px-[16px]
                                tsm-400:px-[18px]
                                sm:px-[23px]
                                py-[8px]
                                tsm-400:py-[9px]
                                sm:py-[18px]
                                sm:w-[330px]
                                lg:w-[382px]
                                w-auto
                                sm:h-[40px]
                                lg:h-[47px]
                                h-auto
                                leading-[12px]
                                tsm-350:leading-[12px]
                                tsm-400:leading-[18px]
                                sm:leading-[22.5px]
                                mb-4
                                tsm-400:mb-6
                                sm:mb-6
                                lg:mb-9
                            "
                        >
                            <div className="bg-white rounded-full flex items-center justify-center p-[2px] sm:p-[3px]">
                                <span className="sm:h-[9px] h-[6px] sm:w-[9px] w-[6px] rounded-full bg-[#0077FF]"></span>
                            </div>
                            <span
                                className="text-[#002F6D] text-[8px] tsm-350:text-[10px] tsm-400:text-[12px] sm:text-[14px] lg:text-[16px] font-semibold"
                                style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.semiBold }}
                            >
                                {pillText}
                            </span>
                        </div>

                        {/* Main Heading */}
                        <h1
                            className="
                                text-[22px]
                                sm:text-[32px]
                                md:text-[35px]
                                lg:text-[48px]
                                xl:text-[60px]
                                text-[#0F172A]
                                mb-3
                                tsm-350:mb-4
                                md:mb-[32px]
                                lg:mb-[43px]
                                tracking-tight
                                max-w-[1135px]
                                leading-[30px]
                                sm:leading-[40px]
                                md:leading-[46px]
                                lg:leading-[55px]
                                xl:leading-[77px]
                            "
                            style={{ fontFamily: "Lato", fontWeight: fontWeights.lato.regular }}
                            dangerouslySetInnerHTML={{ __html: title || '' }}
                        />

                        {/* Description */}
                        <p
                            className="
                                text-[14px]
                                sm:text-[16px]
                                lg:text-[19px]
                                text-[#1A2B4A]
                                mb-4
                                sm:mb-6
                                lg:mb-[31px]
                                xl:mb-[36px]
                                max-w-[1007px]
                                leading-[18px]
                                tsm-400:leading-[20px]
                                sm:leading-[25px]
                                md:leading-[26px]
                                xl:leading-[32px]
                                md:px-4
                                lg:px-6
                                xl:px-0
                            "
                            style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.regular }}
                        >
                            {description}
                        </p>

                        {/* Stats Line */}
                        <div
                            className="
                                flex
                                flex-wrap
                                items-center
                                justify-center
                                gap-6
                                sm:gap-10
                                text-[#64748B]
                            "
                            style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.semiBold }}
                        >
                            <div className="flex flex-col tsm-350:flex-row items-center mb-2 sm:mb-4 md:mb-6 xl:mb-[32px] gap-1 tsm-350:gap-2 tsm-400:gap-3 sm:gap-6 text-[7px] sm:text-[11px] md:text-[14px] tracking-tight text-[#5A6E8C]">
                                {finalStats.map((stat, idx) => (
                                    <span key={idx} className="flex items-center gap-1 tsm-400:gap-1.5 sm:gap-2">
                                        <AllIconsComponent iconName={stat.icon} width="16" height="16" color="#5A6E8C" />
                                        {stat.label}
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div
                            className="
                                flex
                                flex-col
                                tsm-350:flex-row
                                items-center
                                justify-center
                                gap-[8px]
                                tsm-350:gap-[6px]
                                sm:gap-[8px]
                                mb-2
                                tsm-400:mb-[12px]
                                sm:mb-[24px]
                                md:mb-[50px]
                                lg:mb-[100px]
                                xl:mb-[140px]
                                w-full
                                sm:w-auto
                                p-[6px]
                                sm:p-[7px]
                                md:p-[9px]
                                border-[1px]
                                border-[#EBECF0]
                                rounded-[6px]
                                bg-white
                            "
                        >
                            <Button
                                className="
                                    bg-[#002F6D]
                                    hover:bg-[#002F6D]/90
                                    text-white
                                    px-4
                                    sm:px-6
                                    py-1.5
                                    tsm-400:py-[7px]
                                    sm:py-2.5
                                    text-[11px]
                                    tsm-350:text-[13px]
                                    sm:text-[14px]
                                    md:text-[16.3px]
                                    font-regular
                                    rounded-[4px]
                                    shadow-lg
                                    shadow-blue-200
                                    transition-all
                                    w-full
                                    sm:w-auto
                                    h-auto
                                    leading-[24.4px]
                                "
                                style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.medium }}
                                onClick={() => window.dispatchEvent(new CustomEvent("openContactPopup"))}
                            >
                                {hireBtnText || ""}
                            </Button>

                            <Link href={bookDemoLink || ''} target="_blank" className="w-full sm:w-auto">
                                <Button
                                    variant="outline"
                                    className="
                                        bg-[#EBECF0]
                                        text-[#000000]
                                        px-3
                                        sm:px-[15px]
                                        py-1.5
                                        tsm-400:py-[7px]
                                        sm:py-2.5
                                        text-[11px]
                                        tsm-350:text-[13px]
                                        sm:text-[14px]
                                        md:text-[16.3px]
                                        font-regular
                                        rounded-[4px]
                                        hover:bg-slate-300
                                        transition-all
                                        w-full
                                        sm:w-auto
                                        h-auto
                                        border-[1px]
                                        border-black/10
                                        tsm-350:border-none
                                    "
                                    style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.medium }}
                                >
                                    {bookDemoText}
                                </Button>
                            </Link>
                        </div>

                        {/* Mouse Scroll Icon */}
                        <div
                            className="
                                hidden
                                sm:block
                                absolute
                                left-1/2
                                -translate-x-1/2
                                bottom-[40px]
                                lg:static
                                lg:translate-x-0
                                lg:mb-[50px]
                                z-10
                            "
                        >
                            <Image
                                src="/staff_augmentation/smart_solution/mouse_icon.svg"
                                width={24}
                                height={40}
                                alt="Mouse Icon"
                                className="object-contain w-auto h-[40px] mx-auto"
                            />
                        </div>

                    </div>

                    {/* Certification Logos */}
                    <div
                        className="
                            flex
                            flex-col
                            tsm-350:flex-row
                            items-center
                            justify-center
                            gap-4
                            tsm-350:gap-8
                            tsm-400:gap-10
                            sm:gap-6
                            md:gap-8
                            xl:gap-[51px]
                            mt-4
                            sm:mt-0
                            sm:absolute
                            sm:left-[50px]
                            sm:bottom-[30px]
                            md:bottom-[30px]
                            lg:bottom-[40px]
                            xl:bottom-[46px]
                            z-30
                        "
                    >
                        {finalCertificates.map((cert, idx) => (
                            cert && resolveImageUrl(cert) && (
                                <Image
                                    key={idx}
                                    src={resolveImageUrl(cert)}
                                    width={122}
                                    height={150}
                                    alt="Certificate"
                                    className="
                                        object-contain 
                                        w-auto 
                                        h-[120px]
                                        sm:h-[90px]
                                        md:h-[110px]
                                        xl:h-[150px]
                                    "
                                />
                            )
                        ))}
                    </div>

                    {/* Right: Illustration */}
                    <div
                        className="
                        absolute
                        right-0
                        bottom-0
                        z-10
                        hidden
                        sm:block
                        w-[150px]
                        tsm-400:w-[180px]
                        sm:w-[200px]
                        lg:w-[250px]
                        xl:w-[370px]
                        2xl:w-[418px]
                        pointer-events-none
                    "
                    >
                        {workImage && (
                            <Image
                                src={resolveImageUrl(workImage)}
                                width={418}
                                height={329}
                                alt="Work Image"
                                className="
                                object-contain
                                w-full
                                h-auto
                            "
                            />
                        )}
                    </div>

                    {/* Mobile Illustration */}
                    <div className="sm:hidden flex flex-col items-center gap-6 mt-auto">
                        {workImage && (
                            <Image
                                src={resolveImageUrl(workImage)}
                                width={200}
                                height={157}
                                alt="Work Image"
                                className="object-contain w-[180px] h-auto mt-6 sm:mt-0"
                            />
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
